import { DownloadOutlined } from "@ant-design/icons";
import { App, Button, Card, Empty, Skeleton, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { holatVar } from "@/shared/charts";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { useMyExports } from "../../hooks/useReports";
import { reportsApi } from "../../services/reports.api";
import type { ReportDescriptorView, ReportExportJob } from "../../types";
import { reportTitle } from "../../utils/reportI18n";
import { sonMatni } from "./hubFormat";

const { Text } = Typography;

export interface MyExportsPanelProps {
	descriptors: ReportDescriptorView[];
}

/**
 * ⚠️ Holat rangi palitradan: tayyor — `uzrli`, jarayonda — `kelgan`,
 * xato — `kelmagan`. Rang YOLG'IZ emas — yonida matn yoki tugma bor.
 */
const HOLAT_RANGI: Record<ReportExportJob["status"], string> = {
	done: holatVar("uzrli"),
	pending: holatVar("kelgan"),
	processing: holatVar("kelgan"),
	failed: holatVar("kelmagan"),
};

/**
 * Faylni yuklab beradi — `useReportExport.download` bilan bir xil
 * yo'l (`apiClient` orqali, nom serverdan). Hook qayta ishlatilmaydi:
 * u bitta `jobId` holatiga bog'langan.
 */
async function saqlash(job: ReportExportJob): Promise<void> {
	const { blob, filename } = await reportsApi.download(job.id);
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");

	link.href = url;
	link.download = filename ?? `${job.kind}-${job.id}.${job.format}`;
	link.click();

	URL.revokeObjectURL(url);
}

/**
 * «Mening eksportlarim» — oxirgi 7 kun (Task P3 marshruti).
 *
 * ⚠️ Chaqiruvchi `PermissionGate reports:export` ichida chizadi —
 * `useMyExports` shu komponentda, ya'ni ruxsatsiz rolda so'rov umuman
 * ketmaydi (aks holda jim 403).
 */
export function MyExportsPanel({ descriptors }: MyExportsPanelProps) {
	const { t } = useTranslation("reports");
	const { message } = App.useApp();
	const { data, isLoading, error } = useMyExports();

	const nomi = (kind: string): string => {
		const descriptor = descriptors.find((d) => d.kind === kind);

		return descriptor ? reportTitle(t, descriptor) : kind;
	};

	const meta = (job: ReportExportJob): string =>
		[
			job.rowCount !== null ? t("hub.exports.rows", { count: sonMatni(job.rowCount) }) : null,
			dayjs(job.createdAt).format("DD.MM HH:mm"),
		]
			.filter(Boolean)
			.join(" · ");

	return (
		<Card size="small" data-my-exports>
			<div className="mb-1 flex items-center justify-between gap-2">
				<span className="font-semibold text-sm">{t("hub.exports.title")}</span>
				<Text type="secondary" className="text-xs">
					{t("hub.exports.window")}
				</Text>
			</div>

			{isLoading ? <Skeleton active paragraph={{ rows: 2 }} /> : null}

			{error ? (
				<Text type="danger" className="text-xs">
					{getErrorMessage(error, t("hub.exports.loadError"))}
				</Text>
			) : null}

			{data && data.length === 0 ? (
				<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("hub.exports.empty")} />
			) : null}

			{(data ?? []).map((job) => (
				<div
					key={job.id}
					data-export-job={job.status}
					className="flex items-center gap-2.5 border-[var(--chart-grid)] border-t py-2 first-of-type:border-t-0"
				>
					<span
						aria-hidden="true"
						className="h-2 w-2 flex-none rounded-full"
						style={{ backgroundColor: HOLAT_RANGI[job.status] }}
					/>
					<div className="min-w-0 flex-1">
						<div className="truncate font-medium text-[13px]">
							{nomi(job.kind)} · {job.format.toUpperCase()}
						</div>
						<Text type="secondary" className="text-xs">
							{meta(job)}
						</Text>
					</div>

					{job.status === "done" ? (
						<Button
							type="text"
							size="small"
							icon={<DownloadOutlined />}
							aria-label={t("hub.exports.download")}
							onClick={() => {
								saqlash(job).catch((xato: unknown) =>
									message.error(getErrorMessage(xato, t("export.toastDownloadError")))
								);
							}}
						/>
					) : job.status === "failed" ? (
						<Tooltip title={job.error ?? undefined}>
							<span
								data-export-error
								className="whitespace-nowrap text-xs"
								style={{ color: HOLAT_RANGI.failed }}
							>
								{t("hub.exports.failed")}
							</span>
						</Tooltip>
					) : (
						<span className="whitespace-nowrap text-xs" style={{ color: HOLAT_RANGI.pending }}>
							{t("hub.exports.preparing")}
						</span>
					)}
				</div>
			))}
		</Card>
	);
}
